<template>
  <nav
    aria-label="breadcrumb"
    :class="{ 'short-navigation': isShortNavigation }"
  >
    <ArrowBreadcrumbIcon v-if="isShortNavigation" />
    <ol
      class="breadcrumb"
      itemscope
      :itemtype="itemType.breadcrumbList"
    >
      <li
        v-for="(crumb, index) in screenDependentBreadcrumbs"
        :key="index"
        itemprop="itemListElement"
        itemscope
        :itemtype="itemType.listItem"
        @click="clickBreadcrumbHandler"
      >
        <span
          v-if="crumb.isSpan"
          itemprop="item"
          class="breadcrumb-span"
        >
          <span itemprop="name">{{ crumb.label }}</span>
        </span>
        <a
          v-else-if="crumb.external"
          :href="crumb.path"
          itemprop="item"
          class="breadcrumb-link"
        >
          <span itemprop="name">{{ crumb.label }}</span>
        </a>

        <a
          v-else
          tabindex="1"
          :href="crumb?.path as string"
          itemprop="item"
          class="breadcrumb-link"
        >
          <span itemprop="name">{{ crumb.label }}</span>
        </a>

        <ArrowBreadcrumbIcon v-if="showIcon(index)" />
        <meta
          itemprop="position"
          :content="String(index + 1)"
        />
      </li>
    </ol>
  </nav>
</template>

<script setup lang="ts">
  import { useScreenSize } from '@experience/shared';
  import { ArrowBreadcrumbIcon } from '@/shared/ui/icons/arrowBreadcrumbIcon';
  import type { Breadcrumb } from '../../types.ts';

  const itemType = {
    breadcrumbList: 'http://schema.org/BreadcrumbList',
    listItem: 'http://schema.org/ListItem',
  };

  interface Props {
    breadcrumbs: Breadcrumb[];
    isFullBreadcrumbs: boolean;
  }

  const props = defineProps<Props>();

  const { isMobile } = useScreenSize();
  const isShortNavigation = computed(() => isMobile.value);

  const getLastBreadcrumb = (breadcrumbs: Breadcrumb[]): Breadcrumb[] => {
    // переделал код с Array.at(), чтобы была поддержка старых браузеров (ошибка в sentry)
    const lastItem = breadcrumbs && breadcrumbs.length ? breadcrumbs[breadcrumbs.length - 1] : null;

    return lastItem ? [lastItem] : [];
  };

  const screenDependentBreadcrumbs = computed(() => {
    if (isShortNavigation.value) {
      return getLastBreadcrumb(props.breadcrumbs);
    }

    return props.breadcrumbs || [];
  });

  const clickBreadcrumbHandler = () => {
    //   TODO: sogu
  };

  const showIcon = (index: number) => {
    return index !== screenDependentBreadcrumbs.value.length - 1;
  };
</script>

<style scoped lang="scss">
  @use '@experience/design-tokens/typography' as typography;

  nav {
    position: relative;
    display: flex;
    align-items: center;
  }

  .short-navigation {
    :deep(.icon) {
      transform: rotate(180deg);
    }
  }

  .breadcrumb {
    position: relative;
    left: -6px;
    list-style: none;

    @include typography.base-300-300-medium;
  }

  .short-navigation .breadcrumb {
    left: 0;
  }

  .breadcrumb li {
    display: inline;
    align-items: center;
  }

  ol.breadcrumb {
    margin: 0;
    padding: 0;
  }

  .breadcrumb-link,
  .breadcrumb-span {
    padding: 6px;
    color: var(--alias-color-link-darked);
    text-decoration: none;
  }

  .breadcrumb-link:hover {
    color: var(--alias-color-link-default-hovered);
  }

  .breadcrumb-link:focus-visible {
    border-radius: var(--alias-radius-8);
    box-shadow: 0 0 0 5px var(--alias-color-effects-focus);
    outline: 1px solid var(--alias-color-border-focused);
  }
</style>
